import React, { useRef } from 'react';

import { useColorScheme } from '../../hooks/useColorScheme';

// Easing functions for smoother animations
const easeInOutQuad = (timestamp: number) => {
  if (timestamp < 0.5) {
    return 2 * timestamp * timestamp;
  }
  return -1 + (4 - 2 * timestamp) * timestamp;
};

export const CircleProgress: React.FC<{
  size: number;
  strokeWidth: number;
  className?: string;
  onFinish?: () => void;
}> = ({ size, strokeWidth, className, onFinish }) => {
  const { theme } = useColorScheme();
  const hasAnimatedRef = useRef(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Function to animate the progress
  const drawProgress = React.useCallback(
    (ctx: CanvasRenderingContext2D, currentPercentage: number) => {
      // Clear the canvas
      ctx.clearRect(0, 0, size, size);

      // Set up circle parameters
      const radius = (size - strokeWidth) / 2;
      const centerX = size / 2;
      const centerY = size / 2;

      // Draw background circle
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
      ctx.strokeStyle = '#78788029'; // Dark background color
      ctx.lineWidth = strokeWidth;
      ctx.stroke();

      // Draw progress arc
      const endAngle = (currentPercentage / 100) * 2 * Math.PI;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, -Math.PI / 2, endAngle - Math.PI / 2);
      ctx.strokeStyle = '#803EFF'; // Progress color (similar to purple)
      ctx.lineWidth = strokeWidth;
      ctx.lineCap = 'round';
      ctx.stroke();

      // Draw percentage text
      ctx.font = `${size / 5}px Arial`;
      ctx.fillStyle = theme === 'light' ? '#000' : '#fff'; // White text
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`${Math.floor(currentPercentage)}%`, centerX, centerY);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  React.useEffect(() => {
    if (hasAnimatedRef.current) return;
    hasAnimatedRef.current = true;

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    const pixelRatio = window.devicePixelRatio || 1;
    if (ctx && canvas) {
      canvas.width = size * pixelRatio;
      canvas.height = size * pixelRatio;
      canvas.style.width = `${size}px`;
      canvas.style.height = `${size}px`;
      ctx.scale(pixelRatio, pixelRatio); // Scale context to account for pixel ratio

      const ANIMATION_DURATION = 10_000; // 10 seconds in milliseconds
      let startTime: number | null = null;
      let animationId: number;

      // Animation function
      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;

        const elapsed = timestamp - startTime;
        const rawProgress = elapsed / ANIMATION_DURATION;

        // Apply easing function (easeInOutQuad for smooth speed transition)
        const easedProgress = easeInOutQuad(Math.min(rawProgress, 1));

        const percentage = Math.min(easedProgress * 100, 100);

        // Draw progress for current percentage
        drawProgress(ctx, percentage);

        // Continue animating if less than 100%
        if (percentage < 100) {
          animationId = requestAnimationFrame(animate);
        } else {
          cancelAnimationFrame(animationId);
          onFinish && onFinish();
        }
      };

      // Start animation
      animationId = requestAnimationFrame(animate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={className}>
      <canvas ref={canvasRef} />
    </div>
  );
};
